/**
 * The staff console's chat with the analyst of one insight run.
 *
 * Staff only: unlike the customer chat, the analyst's steps travel here too —
 * the queries it ran and what came back — because reading how a report was
 * reached is the point of the page.
 */

/** The staff member who asked a question. */
export interface InsightChatSender {
  id: string;
  name: string;
}

/** A question a staff member put to the analyst. */
export interface InsightChatQuestion {
  kind: 'question';
  id: string;
  text: string;
  /** Null when the person who asked has since been removed. */
  sender: InsightChatSender | null;
  created_at: string;
}

/** What the analyst said. */
export interface InsightChatAnswer {
  kind: 'answer';
  id: string;
  text: string;
  created_at: string;
}

/** A step the analyst took: a query, a design doc read, helpers sent out, notes written. */
export interface InsightChatStep {
  kind: 'step';
  id: string;
  tool: string;
  args: Record<string, unknown>;
  result: string;
  /** Whether `result` was cut short; the full result stays in the conversation. */
  result_truncated: boolean;
  created_at: string;
}

/**
 * A report the analyst wrote. `published` is the run's own report, `corrected`
 * replaced it later, and `shared` is a new report that lives in the chat only.
 */
export interface InsightChatReport {
  kind: 'report';
  id: string;
  action: 'published' | 'corrected' | 'shared';
  title: string | null;
  report: string;
  created_at: string;
}

export type InsightChatItem =
  | InsightChatQuestion
  | InsightChatAnswer
  | InsightChatStep
  | InsightChatReport;

/**
 * Whether a question may be asked now. Anything but `open` holds the composer:
 * `working` until the analyst is done, the other two for good.
 */
export type InsightChatState =
  | 'open'
  | 'working'
  | 'budget_reached'
  | 'conversation_full'
  | 'unavailable';

/** Something new in the conversation. */
export interface InsightChatItemEvent {
  type: 'item';
  item: InsightChatItem;
}

/** A piece of an answer still being written; `text` is appended, never the whole answer. */
export interface InsightChatDeltaEvent {
  type: 'delta';
  message_id: string;
  text: string;
}

/** The run changed — status, report, cost or whether a question may be asked. Read it again. */
export interface InsightChatRunEvent {
  type: 'run_updated';
}

export type InsightChatEvent =
  | InsightChatItemEvent
  | InsightChatDeltaEvent
  | InsightChatRunEvent;

/** The websocket room carrying one run conversation's chat events. */
export function insightChatRoomName(conversationId: string): string {
  return `insight-chat:${conversationId}`;
}
